// Placeholder creation/editing form for UnifiedCourseModal

import { type KeyboardEvent } from 'react';
import { X, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import {
  SOURCE_TYPES,
  PLACEHOLDER_TEMPLATES,
  filterPlaceholderTemplates,
  type PlaceholderTemplate,
} from './constants';
import { useGradeOptions } from '@/hooks/useGradeOptions';
import type { CourseModalContext } from './SearchPanel';
import {
  useInstitutionConfig,
  formatDepartmentLabel,
} from '../../../../contexts/InstitutionConfigContext';

export interface PlaceholderFormRequirements {
  department: string;
  levelMin: number | string;
  levelMax: number | string;
  genEdCategories: string[];
  keywords: string[];
  [key: string]: unknown;
}

export interface PlaceholderFormDataShape {
  title: string;
  credits: number;
  placeholderType: string;
  notes: string;
  sourceType?: string;
  grade?: string;
  institution?: string;
  requirements: PlaceholderFormRequirements;
  [key: string]: unknown;
}

export interface PlaceholderFormErrors {
  title?: string;
  credits?: string;
  level?: string;
  [key: string]: string | undefined;
}

export interface PlaceholderFormProps {
  editingPlaceholder: unknown;
  placeholderData: PlaceholderFormDataShape;
  handleInputChange: (field: string, value: unknown) => void;
  handleTemplateSelect: (template: PlaceholderTemplate) => void;
  handleGenEdToggle: (code: string) => void;
  handleAddKeyword: () => void;
  handleRemoveKeyword: (keyword: string) => void;
  keywordInput: string;
  setKeywordInput: (value: string) => void;
  showRequirements: boolean;
  setShowRequirements: (value: boolean) => void;
  context: CourseModalContext;
  targetSemester?: string | null;
  accentColor: string;
  buttonClass: string;
  errors: PlaceholderFormErrors;
}

const PlaceholderForm = ({
  editingPlaceholder,
  placeholderData,
  handleInputChange,
  handleTemplateSelect,
  handleGenEdToggle,
  handleAddKeyword,
  handleRemoveKeyword,
  keywordInput,
  setKeywordInput,
  showRequirements,
  setShowRequirements,
  context,
  targetSemester,
  accentColor,
  buttonClass,
  errors,
}: PlaceholderFormProps) => {
  const gradeOptions = useGradeOptions();
  // GE area list comes from institution config (workstream E). An EMPTY list
  // means the institution has no general education (UNIC): the GE type option
  // and the GE category picker are hidden rather than rendered empty. (When the
  // backend is unreachable AND no cache exists, the context's FALLBACK_CONFIG
  // still supplies UMD's areas.)
  const { config: institutionConfig } = useInstitutionConfig();
  const genEdCategories = institutionConfig.genEdAreas;
  const hasGenEd = genEdCategories.length > 0;
  // The active institution's departments, not a hardcoded UMD list.
  const departments = institutionConfig.departments ?? [];
  // Hide seed templates whose GE codes (UMD's DSNL/DSHU) or department
  // (UMD's CMSC) the institution doesn't have; the universal templates (no
  // genEdCategories) always show.
  const visibleTemplates = filterPlaceholderTemplates(
    PLACEHOLDER_TEMPLATES,
    genEdCategories.map((a) => a.code),
    departments.map((d) => d.code),
  );
  // A saved placeholder may name a department the list lacks (one saved
  // before this list was per-institution, or a list that has not loaded):
  // keep it selectable so the select shows the stored value, not a blank.
  const savedDepartment = placeholderData.requirements.department;
  const savedDepartmentMissing =
    !!savedDepartment && !departments.some((d) => d.code === savedDepartment);

  const handleKeywordKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  return (
    <div className="space-y-4">
      {!editingPlaceholder && visibleTemplates.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Quick Templates</h3>
          <div className="flex flex-wrap gap-2">
            {visibleTemplates.map((template, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleTemplateSelect(template)}
                className={`px-3 py-1.5 text-sm bg-${accentColor}-50 text-${accentColor}-700 rounded-md hover:bg-${accentColor}-100 transition-colors`}
              >
                {template.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {context === 'other_credits' && (
        <div className="space-y-4 pb-4 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-700">Credit Source</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source Type</label>
              <select
                value={placeholderData.sourceType ?? ''}
                onChange={(e) => handleInputChange('sourceType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-opacity-50 focus:border-transparent"
              >
                {SOURCE_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Grade (optional)
              </label>
              <select
                value={placeholderData.grade ?? ''}
                onChange={(e) => handleInputChange('grade', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-opacity-50 focus:border-transparent"
              >
                {gradeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Institution (optional)
            </label>
            <input
              type="text"
              value={placeholderData.institution ?? ''}
              onChange={(e) => handleInputChange('institution', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-opacity-50 focus:border-transparent"
              placeholder="e.g., Montgomery College"
            />
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">Basic Information</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={placeholderData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-opacity-50 focus:border-transparent ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., Upper-level CS Elective"
            />
            {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Credits</label>
              <input
                type="number"
                value={placeholderData.credits}
                onChange={(e) => handleInputChange('credits', parseFloat(e.target.value) || 0)}
                min="0"
                max="12"
                step="0.1"
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-opacity-50 focus:border-transparent ${
                  errors.credits ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.credits && <p className="mt-1 text-sm text-red-600">{errors.credits}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={placeholderData.placeholderType}
                onChange={(e) => handleInputChange('placeholderType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-opacity-50 focus:border-transparent"
              >
                <option value="elective">Elective</option>
                {(hasGenEd || placeholderData.placeholderType === 'genEd') && (
                  <option value="genEd">General Education</option>
                )}
                <option value="technical">Technical</option>
                <option value="core">Core Requirement</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={placeholderData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-opacity-50 focus:border-transparent"
              placeholder="Any additional notes..."
            />
          </div>
        </div>
      </div>

      <div className="border border-gray-200 rounded-md">
        <button
          type="button"
          onClick={() => setShowRequirements(!showRequirements)}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
        >
          <span className="text-sm font-medium text-gray-700">
            Requirements for Matching
            {(placeholderData.requirements.department ||
              placeholderData.requirements.genEdCategories.length > 0) && (
              <span className="ml-2 text-xs text-gray-500">(configured)</span>
            )}
          </span>
          {showRequirements ? (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
        </button>

        {showRequirements && (
          <div className="px-4 pb-4 space-y-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 pt-3">
              Set requirements to match this placeholder against degree requirements.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select
                value={placeholderData.requirements.department}
                onChange={(e) => handleInputChange('requirements.department', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-opacity-50 focus:border-transparent"
              >
                <option value="">Any Department</option>
                {savedDepartmentMissing && (
                  <option value={savedDepartment}>{savedDepartment}</option>
                )}
                {departments.map((dept) => (
                  <option key={dept.code} value={dept.code}>
                    {formatDepartmentLabel(dept)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course Level Range
              </label>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="number"
                  value={placeholderData.requirements.levelMin}
                  onChange={(e) => handleInputChange('requirements.levelMin', e.target.value)}
                  placeholder="Min (e.g., 300)"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-opacity-50 focus:border-transparent"
                />
                <input
                  type="number"
                  value={placeholderData.requirements.levelMax}
                  onChange={(e) => handleInputChange('requirements.levelMax', e.target.value)}
                  placeholder="Max (e.g., 499)"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-opacity-50 focus:border-transparent"
                />
              </div>
              {errors.level && <p className="mt-1 text-sm text-red-600">{errors.level}</p>}
            </div>

            {hasGenEd && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                General Education Categories
              </label>
              <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto">
                {genEdCategories.map((category) => (
                  <label
                    key={category.code}
                    className="flex items-center gap-2 py-1 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={placeholderData.requirements.genEdCategories.includes(category.code)}
                      onChange={() => handleGenEdToggle(category.code)}
                      className="rounded focus:ring-opacity-50"
                    />
                    <span className="text-sm">
                      <span className="font-medium">{category.code}</span> - {category.name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Keywords (optional)
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyPress={handleKeywordKeyPress}
                  placeholder="Add keyword..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-opacity-50 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={handleAddKeyword}
                  className={`px-4 py-2 ${buttonClass} text-white rounded-md transition-colors`}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              {placeholderData.requirements.keywords.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {placeholderData.requirements.keywords.map((keyword, idx) => (
                    <span
                      key={idx}
                      className={`inline-flex items-center gap-1 px-2 py-1 bg-${accentColor}-100 text-${accentColor}-700 rounded-md text-sm`}
                    >
                      {keyword}
                      <button
                        type="button"
                        onClick={() => handleRemoveKeyword(keyword)}
                        className={`hover:text-${accentColor}-900`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {targetSemester && (
        <div className={`bg-${accentColor}-50 border border-${accentColor}-200 rounded-md p-3`}>
          <p className="text-sm text-gray-700">
            This will be added to{' '}
            <strong>{targetSemester.replace('-', ' ').toUpperCase()}</strong>
          </p>
        </div>
      )}
    </div>
  );
};

export default PlaceholderForm;
